import React, {
    type ComponentProps,
    type FC,
    type ReactNode,
    memo,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";
import { cn, Description, FieldError, Input, Label, TextField } from "@heroui/react";
import { Utils } from "@binale-tech/shared";
import { type CurrencyValue } from "@domain/models/CurrencyValue";

export type { CurrencyValue };

const { CurrencyFormatter, currencyFormat } = Utils.CurrencyUtils;
const MAX_VALUE = Number.MAX_SAFE_INTEGER;

interface HeroBruttoInputProps extends Omit<ComponentProps<typeof Input>, "value" | "onChange" | "onBlur" | "onFocus"> {
    value?: number;
    onChange?: (value: CurrencyValue) => void;
    onBlur?: (value: CurrencyValue) => void;
    onFocus?: (value: CurrencyValue) => void;
    maxValue?: number;
    label?: ReactNode;
    description?: ReactNode;
    errorMessage?: ReactNode;
    isInvalid?: boolean;
    isRequired?: boolean;
    isDisabled?: boolean;
    isReadOnly?: boolean;
    startContent?: ReactNode;
    endContent?: ReactNode;
    classNames?: Record<string, string>;
}

type InternalState = {
    text: string;
    brutto: number | null;
    digits: number;
};

function deriveStateFromValue(value: number | undefined, digits: number): InternalState {
    const text = !value ? "" : currencyFormat(value, digits, digits === 0);
    return { brutto: value ?? null, text, digits };
}

export const AmountInput: FC<HeroBruttoInputProps> = memo(
    ({
        value,
        onChange,
        onBlur,
        onFocus,
        placeholder = currencyFormat(0, CurrencyFormatter.PRECISION),
        maxLength = 14,
        maxValue,
        classNames,
        startContent,
        endContent,
        ref,
        label,
        description,
        errorMessage,
        isInvalid,
        isRequired,
        isDisabled,
        isReadOnly,
        ...rest
    }) => {
        const inputRef = useRef<HTMLInputElement>(null);
        useImperativeHandle(ref, () => inputRef.current, []);
        const [state, setState] = useState<InternalState>(() =>
            deriveStateFromValue(value, CurrencyFormatter.PRECISION),
        );
        // track whether the current change should fire onChange
        const isValueUpdatedRef = useRef(true);

        // sync external value -> internal state
        useEffect(() => {
            setState((prev) => deriveStateFromValue(value, prev.digits));
        }, [value]);

        const processChange = useCallback(
            (newText: string, oldText: string, selectionStart: number, selectionEnd: number): InternalState => {
                let changedText = oldText;
                let digits = 0;
                isValueUpdatedRef.current = true;

                if (oldText !== newText) {
                    // intermediate zero / negative-zero states
                    if (newText === "0" || newText === "0," || newText === "0,0") {
                        changedText = newText;
                        isValueUpdatedRef.current = false;
                    } else if (newText === "-" || newText === "-0" || newText === "-0," || newText === "-0,0") {
                        changedText = newText;
                        isValueUpdatedRef.current = false;
                    } else if (oldText.length < newText.length) {
                        // character added
                        const changeText = newText.substring(selectionStart - 1, selectionEnd);
                        if (/[0-9,.-]/.test(changeText)) {
                            const hasMinus = oldText.includes("-");
                            const num = CurrencyFormatter.textToCents(newText);
                            switch (changeText) {
                                case CurrencyFormatter.THOUSANDS_SEPARATOR:
                                    break;
                                case "-":
                                    changedText = hasMinus ? oldText.replace("-", "") : "-" + oldText;
                                    isValueUpdatedRef.current = oldText !== "";
                                    digits = oldText.length - 1 - oldText.indexOf(CurrencyFormatter.DECIMAL_SEPARATOR);
                                    digits = digits === oldText.length ? 0 : digits;
                                    break;
                                case CurrencyFormatter.DECIMAL_SEPARATOR:
                                    if (
                                        oldText !== "" &&
                                        !oldText.includes(CurrencyFormatter.DECIMAL_SEPARATOR) &&
                                        newText.indexOf(CurrencyFormatter.DECIMAL_SEPARATOR) === oldText.length
                                    ) {
                                        changedText = oldText + CurrencyFormatter.DECIMAL_SEPARATOR;
                                        isValueUpdatedRef.current = false;
                                    }
                                    break;
                                default:
                                    digits = 0;
                                    if (oldText !== "" && newText.includes(CurrencyFormatter.DECIMAL_SEPARATOR)) {
                                        const yetFractionDigits =
                                            oldText.length - 1 - oldText.indexOf(CurrencyFormatter.DECIMAL_SEPARATOR);
                                        if (yetFractionDigits >= CurrencyFormatter.PRECISION) {
                                            // already at max decimal digits - block the input
                                            changedText = oldText;
                                            digits = CurrencyFormatter.PRECISION;
                                            isValueUpdatedRef.current = false;
                                            break;
                                        } else {
                                            digits =
                                                yetFractionDigits < CurrencyFormatter.PRECISION
                                                    ? yetFractionDigits + 1
                                                    : 0;
                                        }
                                    }
                                    if (num <= (maxValue ?? MAX_VALUE)) {
                                        changedText = currencyFormat(num, digits, digits === 0);
                                    }
                                    break;
                            }
                        }
                    } else {
                        // character deleted
                        digits = newText.includes(CurrencyFormatter.DECIMAL_SEPARATOR)
                            ? newText.length - 1 - newText.indexOf(CurrencyFormatter.DECIMAL_SEPARATOR)
                            : 0;
                        const num = CurrencyFormatter.textToCents(newText);
                        const lastChar = newText.charAt(newText.length - 1);
                        if (oldText.length > newText.length && lastChar === CurrencyFormatter.DECIMAL_SEPARATOR) {
                            changedText = newText === "" ? "" : newText;
                            isValueUpdatedRef.current = false;
                        } else {
                            changedText = newText === "" ? "" : currencyFormat(num, digits, digits === 0);
                        }
                    }
                }

                const text = changedText;
                const brutto = text === "" ? null : CurrencyFormatter.textToCents(text);
                return { text, digits, brutto: brutto || null };
            },
            [maxValue],
        );

        const handleChange = useCallback(
            (newText: string) => {
                const input = inputRef.current;
                const selectionStart = input?.selectionStart || 0;
                const selectionEnd = input?.selectionEnd || 0;

                const newState = processChange(newText, state.text, selectionStart, selectionEnd);
                setState(newState);

                if (onChange && isValueUpdatedRef.current) {
                    onChange({ amount: newState.brutto });
                }
            },
            [onChange, processChange, state.text],
        );

        const handleBlur = useCallback(() => {
            const digits = CurrencyFormatter.PRECISION;
            const brutto = state.text === "" ? null : CurrencyFormatter.textToCents(state.text);
            const text = !brutto ? "" : currencyFormat(brutto, digits, false);
            setState({ digits, text, brutto: brutto || null });

            if (onChange) {
                onChange({ amount: brutto });
            }
            if (onBlur) {
                onBlur({ amount: brutto });
            }
        }, [onChange, onBlur, state.text]);

        const handleFocus = useCallback(() => {
            inputRef.current?.select();
            if (onFocus) {
                onFocus({ amount: state.brutto || 0 });
            }
        }, [onFocus, state.brutto]);

        const inputClassName = cn("text-right", classNames?.inputWrapper, classNames?.input, rest.className);

        const baseInput = (
            <Input
                {...rest}
                className={inputClassName}
                ref={inputRef}
                type="text"
                placeholder={placeholder}
                maxLength={maxLength}
                onBlur={handleBlur}
                onFocus={handleFocus}
                disabled={isDisabled}
                readOnly={isReadOnly}
            />
        );

        const inputEl =
            startContent || endContent ? (
                <div className="relative">
                    {startContent && (
                        <div className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                            {startContent}
                        </div>
                    )}
                    <Input
                        {...rest}
                        className={cn(inputClassName, "w-full min-w-0", {
                            "pl-8": startContent,
                            "pr-12": endContent,
                        })}
                        ref={inputRef}
                        type="text"
                        placeholder={placeholder}
                        maxLength={maxLength}
                        onBlur={handleBlur}
                        onFocus={handleFocus}
                        disabled={isDisabled}
                        readOnly={isReadOnly}
                    />
                    {endContent && (
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                            {endContent}
                        </div>
                    )}
                </div>
            ) : (
                baseInput
            );

        if (!label && !description && !errorMessage) {
            return (
                <TextField
                    aria-label="Amount"
                    className="min-w-0 w-full"
                    value={state.text || ""}
                    onChange={handleChange}
                    isInvalid={isInvalid}
                    isRequired={isRequired}
                    isDisabled={isDisabled}
                    isReadOnly={isReadOnly}
                >
                    {inputEl}
                </TextField>
            );
        }

        return (
            <TextField
                aria-label="Amount input"
                value={state.text || ""}
                onChange={handleChange}
                isInvalid={isInvalid}
                isRequired={isRequired}
                isDisabled={isDisabled}
                isReadOnly={isReadOnly}
            >
                {label && <Label>{label}</Label>}
                {inputEl}
                {description && <Description>{description}</Description>}
                {errorMessage && <FieldError>{errorMessage}</FieldError>}
            </TextField>
        );
    },
);
